import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/pages/portal/components/PortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import { changeUserPassword } from "@/services/authService";
import { ApiError } from "@/services/http";

export default function PortalProfilePage() {
  const { user, accessToken, isDemoMode } = useAuth();

  // Password change state
  const [currPass,  setCurrPass]  = useState("");
  const [newPass,   setNewPass]   = useState("");
  const [confPass,  setConfPass]  = useState("");
  const [passErr,   setPassErr]   = useState("");
  const [passOk,    setPassOk]    = useState(false);
  const [passLoad,  setPassLoad]  = useState(false);

  async function handlePasswordChange(e: FormEvent) {
    e.preventDefault();
    setPassErr(""); setPassOk(false);

    if (newPass !== confPass)  { setPassErr("New passwords do not match."); return; }
    if (newPass.length < 8)   { setPassErr("Password must be at least 8 characters."); return; }

    setPassLoad(true);
    try {
      if (isDemoMode) {
        await new Promise((r) => setTimeout(r, 600));
        setPassOk(true);
        setCurrPass(""); setNewPass(""); setConfPass("");
        return;
      }
      await changeUserPassword(accessToken!, currPass, newPass);
      setPassOk(true);
      setCurrPass(""); setNewPass(""); setConfPass("");
    } catch (err) {
      setPassErr(err instanceof ApiError ? err.message : "Could not update password. Please try again.");
    } finally {
      setPassLoad(false);
    }
  }

  const inputClass =
    "h-11 w-full rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground-600";

  const initials = (user?.name ?? "?")
    .split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const planBadge: Record<string, string> = {
    admin:   "bg-rose-100 text-rose-700",
    owner:   "bg-primary-100 text-primary-700",
    user:    "bg-background-200 text-foreground-600",
  };

  return (
    <PortalLayout>
      <div className="max-w-2xl">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Account Settings</h1>
          <p className="mt-1 text-sm text-foreground-600">Manage your account details and security.</p>
        </div>

        {/* Profile card */}
        <div className="mb-6 rounded-xl border border-background-200 bg-background-50 p-6">
          <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">Profile</h2>
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary-100 text-2xl font-bold text-primary-700">
              {initials}
            </div>
            <div>
              <p className="text-base font-bold text-foreground-950">{user?.name}</p>
              <p className="text-sm text-foreground-600">{user?.email}</p>
              {user?.phone && (
                <p className="mt-0.5 text-sm text-foreground-500">{user.phone}</p>
              )}
              <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${planBadge[user?.role ?? "user"]}`}>
                {user?.role}
              </span>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 border-t border-background-200 pt-5 sm:grid-cols-2">
            <div>
              <p className={labelClass}>Full Name</p>
              <p className="h-11 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-700">
                {user?.name}
              </p>
            </div>
            <div>
              <p className={labelClass}>Email Address</p>
              <p className="h-11 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-700">
                {user?.email}
              </p>
            </div>
          </div>
          {isDemoMode && (
            <p className="mt-3 text-xs text-foreground-500">
              <i className="ri-information-line mr-1" />
              Profile editing requires a live backend connection.
            </p>
          )}
        </div>

        {/* Plan info */}
        <div className="mb-6 rounded-xl border border-background-200 bg-background-50 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">Listing Plan</h2>
              <p className="mt-1 text-sm text-foreground-600">Your current listing tier and benefits.</p>
            </div>
            <Link to="/listing-plans"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100">
              <i className="ri-arrow-right-up-line text-sm" /> View Plans
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { icon: "ri-store-3-line",     slug: "free",     plan: "Free",     active: true,  desc: "Free for 12 months · 3 photos" },
              { icon: "ri-rocket-2-line",    slug: "business", plan: "Business", active: false, desc: "₹599/year · Lead alerts" },
              { icon: "ri-vip-crown-2-line", slug: "premium",  plan: "Premium",  active: false, desc: "₹1,599/year · Featured" },
            ].map((p) => (
              <div key={p.plan}
                className={`flex flex-1 items-center gap-3 rounded-lg border p-4 transition-shadow ${
                  p.active
                    ? "border-primary-300 bg-primary-50"
                    : "border-background-200 bg-background-50 hover:border-primary-200"
                }`}>
                <span className={`flex h-8 w-8 items-center justify-center rounded-md ${p.active ? "bg-primary-100" : "bg-background-200"}`}>
                  <i className={`${p.icon} text-base ${p.active ? "text-primary-600" : "text-foreground-500"}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-bold ${p.active ? "text-primary-700" : "text-foreground-700"}`}>{p.plan}</p>
                  <p className="text-[11px] text-foreground-500">{p.desc}</p>
                </div>
                {p.active
                  ? <span className="ml-auto shrink-0 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold text-background-50">Active</span>
                  : (
                    <Link
                      to={`/listing-plans#plan-${p.slug}`}
                      className="ml-auto shrink-0 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      Upgrade
                    </Link>
                  )}
              </div>
            ))}
          </div>
        </div>

        {/* Password change */}
        <div className="rounded-xl border border-background-200 bg-background-50 p-6">
          <h2 className="mb-1 font-heading text-base font-bold text-foreground-950">Change Password</h2>
          <p className="mb-5 text-sm text-foreground-600">Use a strong password with at least 8 characters.</p>

          <form onSubmit={handlePasswordChange} className="flex flex-col gap-4">
            <div>
              <label className={labelClass} htmlFor="curr-pass">Current password</label>
              <input id="curr-pass" type="password" required value={currPass}
                onChange={(e) => setCurrPass(e.target.value)} placeholder="••••••••" className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="new-pass">New password</label>
              <input id="new-pass" type="password" required value={newPass}
                onChange={(e) => setNewPass(e.target.value)} placeholder="Min. 8 characters" className={inputClass} />
            </div>
            <div>
              <label className={labelClass} htmlFor="conf-pass">Confirm new password</label>
              <input id="conf-pass" type="password" required value={confPass}
                onChange={(e) => setConfPass(e.target.value)} placeholder="Repeat new password" className={inputClass} />
            </div>

            {passErr && (
              <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />{passErr}
              </div>
            )}
            {passOk && (
              <div className="flex items-center gap-2.5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                <i className="ri-checkbox-circle-line text-base" /> Password updated successfully.
              </div>
            )}

            <button type="submit" disabled={passLoad}
              className="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:self-start">
              {passLoad ? (
                <><i className="ri-loader-4-line animate-spin text-base" /> Updating…</>
              ) : (
                <><i className="ri-lock-password-line text-base" /> Update Password</>
              )}
            </button>
          </form>
        </div>
      </div>
    </PortalLayout>
  );
}